
import { UploadOperationComponent } from "./upload-operation/upload-operation.component";
import {Component, OnDestroy, OnInit} from "@angular/core";
import {DynamicTableComponent} from "../../shared/components/dynamic-table/dynamic-table.component";
import {ToolbarComponent} from "../../shared/components/toolbar/toolbar.component";
import {IOperation} from "../../core/models/operation.model";
import {take} from "rxjs";
import {CustomDialogData} from "../../core/models/custom-dialog.model";
import {customDynamicDialog, DEFAULT_ROWS_PER_PAGE_OPTIONS} from "../../core/utils/utils";
import {CustomAction, CustomTableHeader} from "../../core/models/custom-table.model";
import {OperationService} from "../../core/services/operation.service";
import {ToastService} from "../../core/services/toast.service";
import {DialogService, DynamicDialogRef} from "primeng/dynamicdialog";
import {StorageService} from "../../core/services/storage.service";
import {getDefaultPageable, Pageable, PageParams} from "../../core/models/page.model";
import {PaginatorState} from "primeng/paginator";
import {FormBuilder, FormGroup, ReactiveFormsModule} from "@angular/forms";
import {IAccount} from "../../core/models/account.model";
import {ICurrency} from "../../core/models/devise.model";
import {IInstitution} from "../../core/models/institution.model";
import {AccountService} from "../../core/services/account.service";
import {DeviseService} from "../../core/services/devise.service";
import {InstitutionService} from "../../core/services/institution.service";
import {CardModule} from "primeng/card";
import {SelectModule} from "primeng/select";
import {DatePickerModule} from "primeng/datepicker";
import {ButtonModule} from "primeng/button";
import {TooltipModule} from "primeng/tooltip";
import {DateRangeComponent} from "../../shared/date-range/date-range.component";
import {CommonModule} from "@angular/common";
import {ProgressSpinnerModule} from "primeng/progressspinner";

@Component({
    selector: 'app-operation',
    imports: [
        DynamicTableComponent,
        ToolbarComponent,
        ReactiveFormsModule,
        CardModule,
        SelectModule,
        DatePickerModule,
        ButtonModule,
        TooltipModule,
        DateRangeComponent,
        CommonModule,
        ProgressSpinnerModule
    ],
    templateUrl: './operation.component.html',
    styleUrl: './operation.component.scss'
})
export class OperationComponent implements OnInit, OnDestroy {

  operations: IOperation[] = [];
  columns: CustomTableHeader[] = [
    { key: 'account.institutionName', column: 'Institution', type: 'text' },
    { key: 'state', column: 'Status', type: 'text' },
    { key: 'operationDate', column: 'Date opération', type: 'date' },
    { key: 'valueDate', column: 'Date valeur', type: 'date' },
    { key: 'libelle', column: 'Libelle', type: 'text' },
    { key: 'amount', column: 'Montant', type: 'currency', currencyKey: 'devise.code' },
    { key: 'account.libelle', column: 'Compte', type: 'text' },
    { key: 'sens', column: 'Sens', type: 'text' },
    { key: 'currency.code', column: 'Devise', type: 'text' }
  ];
  pageable: PageParams = {page: 0, first: 0, rows: 0, totalRecords: 0, rowPerPageOptions: []};
  ref: DynamicDialogRef | null = null;
  actions: CustomAction[] = [];
  toolbarActions: CustomAction[] = [];

  // Formulaire de recherche
  searchForm!: FormGroup;
  institutions: IInstitution[] = [];
  accounts: IAccount[] = [];
  allAccounts: IAccount[] = []; // Pour stocker tous les comptes
  currencies: ICurrency[] = [];
  rangeDates: Date[] = [];
  minDate: Date | null = null;
  hasSearched: boolean = false;

  displaySpinner = false

  dateTypes = [
    { label: 'Date d\'opération', value: 'operation' },
    { label: 'Date de valeur', value: 'valeur' }
  ];

  sensOptions = [
    { label: 'Crédit', value: 'CREDIT' },
    { label: 'Débit', value: 'DEBIT' }
  ];

  constructor(
    private operationService: OperationService,
    private toastService: ToastService,
    public dialogService: DialogService,
    private fb: FormBuilder,
    private accountService: AccountService,
    private deviseService: DeviseService,
    private institutionService: InstitutionService
  ) {}

  ngOnInit() {
    this.initSearchForm();
    this.loadInstitutions();
    this.loadAccounts();
    this.loadCurrencies();
    this.actions = this.getActions();
    this.toolbarActions = this.getToolbarActions();
  }

  initSearchForm() {
    this.searchForm = this.fb.group({
      institutionId: [null],
      accountId: [null],
      currencyCode: [null],
      sens: [null]
    });

    // Écouter les changements sur institutionName pour filtrer les comptes
    this.searchForm.get('institutionName')?.valueChanges.subscribe(institutionName => {
      this.onInstitutionChange(institutionName);
    });
  }

  onDateRangeSelected(dateRange: Date[]) {
    if (dateRange.length === 2 && dateRange[0] && dateRange[1]) {
      this.rangeDates = dateRange;
      this.minDate = dateRange[0];
    }
  }

  onInvalidRange(message: string) {
    this.toastService.showToast('Recherche d\'opérations', message, 'error');
  }

  loadInstitutions() {
    this.institutionService.getAllInstitutions(getDefaultPageable(0, 1000)).subscribe({
      next: page => {
        this.institutions = page.content;
      },
      error: err => this.toastService.showToast('Chargement des institutions', err.error, 'error')
    });
  }

  loadAccounts() {
    this.accountService.getAllAccounts(getDefaultPageable(0, 1000)).subscribe({
      next: page => {
        this.allAccounts = page.content;
        this.accounts = page.content; // Initialement, afficher tous les comptes
      },
      error: err => this.toastService.showToast('Chargement des comptes', err.error, 'error')
    });
  }

  onInstitutionChange(institutionName: string | null) {
    if (institutionName) {
      // Filtrer les comptes par nom d'institution
      this.accounts = this.allAccounts.filter(account =>
        account.institutionName === institutionName
      );
    } else {
      // Si aucune institution n'est sélectionnée, afficher tous les comptes
      this.accounts = this.allAccounts;
    }

    // Réinitialiser le compte sélectionné si le compte actuel n'est plus dans la liste filtrée
    const currentAccountId = this.searchForm.get('accountId')?.value;
    if (currentAccountId) {
      const accountExists = this.accounts.some(acc => acc.id === currentAccountId);
      if (!accountExists) {
        this.searchForm.patchValue({ accountId: null });
      }
    }
  }

  loadCurrencies() {
    this.deviseService.getAllDevises(getDefaultPageable(0, 1000)).subscribe({
      next: page => {
        this.currencies = page.content;
      },
      error: err => this.toastService.showToast('Chargement des devises', err.error, 'error')
    });
  }

  ngOnDestroy() {
    this.ref?.close();
  }

  getToolbarActions(): CustomAction[] {
    return [
      {
        role: 'CREATE_OPERATION',
        icon: 'pi pi-plus',
        label: 'Créer',
        severity: 'primary',
        emit: () => this.createOperation(),
        visible: true
      },
      {
        role: 'CREATE_OPERATION',
        icon: 'pi pi-download',
        label: 'Charger',
        severity: 'primary',
        emit: () => this.uploadOperation(),
        visible: true
      },
      {
        role: 'READ_OPERATION',
        icon: 'pi pi-upload',
        label: 'Exporter',
        severity: 'info',
        emit: () => this.loadOperations(),
        visible: true
      }
    ];
  }

  getActions(): CustomAction[] {
    return [
      {
        role: 'READ_OPERATION',
        icon: 'pi pi-info-circle',
        label: 'Consulter',
        severity: 'info',
        emit: (item) => this.viewOperation(item),
        visible: true
      },
      {
        role: 'UPDATE_OPERATION',
        icon: 'pi pi-pen-to-square',
        label: 'Edit',
        severity: 'primary',
        emit: (item) => this.editOperation(item),
        visible: true
      },
      {
        role: 'DELETE_OPERATION',
        icon: 'pi pi-trash',
        label: 'Supprimer',
        severity: 'danger',
        emit: (item) => this.deleteOperation(item),
        visible: true
      },
    ];
  }

  loadOperations(pageable: Pageable = getDefaultPageable()) {
    const formValue = this.searchForm?.value;

    // Si aucun filtre n'est appliqué, charger toutes les opérations
    if (!formValue || this.isSearchFormEmpty()) {
      this.displaySpinner = true;

      this.operationService.getOperations(pageable).subscribe({
        next: page => {
          this.displaySpinner = false;
          this.operations = page.content;
          this.pageable = {
            page: page.number,
            first: page.number * page.size,
            rows: page.size,
            totalRecords: page.totalElements,
            rowPerPageOptions: DEFAULT_ROWS_PER_PAGE_OPTIONS
          };
        },
        error: err => this.toastService.showToast('Chargement des opérations', err.error, 'error')
      });
    } else {
      // Recherche avec filtres
      this.searchOperations(pageable);
    }
  }

  isSearchFormEmpty(): boolean {
    const formValue = this.searchForm.value;
    return !formValue.accountId &&
           this.rangeDates.length === 0 &&
           !formValue.institutionId &&
           !formValue.currencyCode &&
           !formValue.sens;
  }

  searchOperations(pageable: Pageable = getDefaultPageable()) {
    const formValue = this.searchForm.value;
    const startDate = this.rangeDates[0]?.toLocaleDateString('en-CA');
    const endDate = this.rangeDates[1]?.toLocaleDateString('en-CA');
    this.displaySpinner = true;

    this.operationService.searchOperations(
      formValue.accountId,
      startDate,
      endDate,
      formValue.institutionId,
      formValue.currencyCode,
      formValue.sens,
      pageable
    ).subscribe({
      next: page => {
        this.displaySpinner = false;
        this.operations = page.content;
        this.pageable = {
          page: page.number,
          first: page.number * page.size,
          rows: page.size,
          totalRecords: page.totalElements,
          rowPerPageOptions: DEFAULT_ROWS_PER_PAGE_OPTIONS
        };
      },
      error: err => this.toastService.showToast('Recherche des opérations', err.error, 'error')
    });
  }

  onSearch() {
    this.hasSearched = true;
    this.loadOperations();
  }

  onReset() {
    this.searchForm.reset();
    this.rangeDates = [];
    this.minDate = null;
    this.hasSearched = false;
    this.operations = [];
    this.accounts = this.allAccounts; // Réinitialiser la liste des comptes
  }

  viewOperation(operation: IOperation) {
    const data = {isView: true, data: operation} as CustomDialogData;
    this.ref = this.dialogService.open(UploadOperationComponent, customDynamicDialog('Détails opération', data));
    this.ref?.onClose.pipe(take(1)).subscribe(() => this.loadOperations());
  }

  editOperation(operation: IOperation) {
    const data = {isEdit: true, data: operation} as CustomDialogData;
    this.ref = this.dialogService.open(UploadOperationComponent, customDynamicDialog('Modification opération', data));
    this.ref?.onClose.pipe(take(1)).subscribe(() => this.loadOperations());
  }

  createOperation() {
    const data = {isCreate: true} as CustomDialogData;
    this.ref = this.dialogService.open(UploadOperationComponent, customDynamicDialog('Création opération', data));
    this.ref?.onClose.pipe(take(1)).subscribe(() => this.loadOperations());
  }

  deleteOperation(operation: IOperation) {
    const summary = 'Opération supprimée';
    this.operationService.deleteOperation(operation.id!).subscribe({
      next: () => {
        this.toastService.showToast(summary, `L'opération ${operation.libelle} a été supprimée avec succès.`);
        this.loadOperations();
      },
      error: err => this.toastService.showToast(summary, err.error, 'error')
    });
  }

  uploadOperation() {
    const options = { position: 'center', width: '75vw', height: '75vh' };
    this.ref = this.dialogService.open(UploadOperationComponent, customDynamicDialog('Chargement d\'opérations', {}, options));
    this.ref?.onClose.pipe(take(1)).subscribe(() => this.loadOperations());
  }

  onPageChange = (event: PaginatorState) => this.loadOperations(getDefaultPageable(event.page, event.rows));
}
